import { useEffect, useRef, useState } from 'react';
function eventStartedInside(event, element) {
    const { target } = event;
    if (target instanceof Node && element.contains(target)) {
        return true;
    }
    return event.composedPath().includes(element);
}
/**
 * Hook that closes a hover overlay when the page scrolls.
 *
 * Radix HoverCard doesn't have built-in scroll-to-close behavior like Tooltip does.
 * This hook provides that behavior by returning a flag to override the `open` prop:
 *
 * ```tsx
 * <HoverCardPrimitive.Root open={isOpen && !isClosedByScroll} ... />
 * ```
 *
 * The challenge with HoverCard is that it maintains internal hover state. Simply
 * calling `onOpenChange(false)` doesn't work because HoverCard immediately
 * tries to re-open when it detects the pointer is still over the trigger.
 *
 * This hook solves that by keeping the overlay closed until the user naturally
 * moves their pointer away (causing `isOpen` to become false), at which point the
 * flag resets and normal hover behavior resumes.
 *
 * @see https://github.com/radix-ui/primitives/blob/907513701a75b11a115563f9554ac6e8147bf2db/packages/react/tooltip/src/tooltip.tsx#L528-L537
 */
export function useCloseOnScroll({ isOpen, enabled = true, onOpenChange, ignoredScrollContainerRef, }) {
    const [isClosedByScroll, setIsClosedByScroll] = useState(false);
    const isOpenRef = useRef(isOpen);
    const onOpenChangeRef = useRef(onOpenChange);
    isOpenRef.current = isOpen;
    onOpenChangeRef.current = onOpenChange;
    const resetFlagOnClose = () => {
        if (!isOpen || !enabled) {
            setIsClosedByScroll(false);
        }
    };
    useEffect(resetFlagOnClose, [isOpen, enabled]);
    useEffect(() => {
        if (!enabled)
            return;
        const forceCloseOnScroll = (event) => {
            if (!isOpenRef.current) {
                return;
            }
            const ignoredScrollContainer = ignoredScrollContainerRef?.current;
            if (ignoredScrollContainer && eventStartedInside(event, ignoredScrollContainer)) {
                return;
            }
            setIsClosedByScroll(true);
            onOpenChangeRef.current(false);
        };
        window.addEventListener('scroll', forceCloseOnScroll, { capture: true });
        return () => {
            window.removeEventListener('scroll', forceCloseOnScroll, { capture: true });
        };
    }, [ignoredScrollContainerRef, enabled]);
    return { isClosedByScroll: enabled && isClosedByScroll };
}
//# sourceMappingURL=useCloseOnScroll.js.map